import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from '../../components/ui/use-toast';
import { migrateCategoriesToSingleCollection } from '../../utils/categoryMigration';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';

interface CategoryMigrationToolProps {}

const CategoryMigrationTool: React.FC<CategoryMigrationToolProps> = () => {
  const [migrating, setMigrating] = useState(false);
  const [result, setResult] = useState<{
    success?: boolean;
    migratedCategories?: number;
    totalCategories?: number;
    error?: any;
  } | null>(null);

  const handleMigration = async () => {
    try {
      setMigrating(true);
      setResult(null);
      
      const migrationResult = await migrateCategoriesToSingleCollection();
      setResult(migrationResult);
      
      if (migrationResult.success) {
        toast({
          title: "Migration Successful",
          description: `Migrated ${migrationResult.migratedCategories} categories to the new structure.`,
        });
      } else {
        toast({
          title: "Migration Failed",
          description: "There was an error during migration. Check console for details.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Migration error:', error);
      setResult({ success: false, error });
      toast({
        title: "Migration Error",
        description: "An unexpected error occurred during migration.",
        variant: "destructive",
      });
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="space-y-4 p-4 border rounded-md bg-white dark:bg-gray-800">
      <h3 className="text-lg font-medium">Category Migration Tool</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        This tool migrates categories from the old userFavorites collection to the new sopCategories collection.
        This is a one-time operation to improve the data structure.
      </p>
      
      {result && (
        <Alert variant={result.success ? "default" : "destructive"}>
          <AlertTitle>{result.success ? "Migration Successful" : "Migration Failed"}</AlertTitle>
          <AlertDescription>
            {result.success 
              ? `Successfully migrated ${result.migratedCategories} out of ${result.totalCategories} categories.` 
              : "Failed to migrate categories. Check console for details."}
          </AlertDescription>
        </Alert>
      )}
      
      <Button
        onClick={handleMigration}
        disabled={migrating}
        className="bg-blue-600 hover:bg-blue-700 text-white"
      >
        {migrating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Migrating...
          </>
        ) : (
          "Migrate Categories to Single Collection"
        )}
      </Button>
    </div>
  );
};

export default CategoryMigrationTool;